import { useCallback, useSyncExternalStore } from 'react';

/**
 * DEBUG view: scale each matrix cell by its reliance weight, centred in its slot.
 *
 * A diagnostic, not a product view. The default rendering keeps every cell
 * full-bleed with a strength bar, because a grid of differently-sized boxes is
 * harder to scan than a grid of equal ones. But when you are asking "is the
 * weighting sane?", relative size is the fastest way to see it — a cell holding
 * 1% of a business function's credit should look nothing like one holding 30%.
 *
 * Same storage pattern as `matrix-grounding.ts` (localStorage +
 * `useSyncExternalStore`): survives reload, syncs across tabs, no provider.
 *
 * NOTE ON WHAT IT IS SHOWING. It renders `function_weights`, which today carries
 * the HEAD file-overlap mass from the first cut. That is the wrong evidence for
 * reliance (see matrix-axis-projection-model.md §2.4.1) and will be replaced by
 * commit-intersection reliance. Until then this visualises the number we have,
 * not the number we want — which is precisely why it is labelled debug.
 */

const KEY = 'ta_matrix_cell_scale';
const EVENT = 'ta:matrix-cell-scale';

export function getStoredCellScale(): boolean {
  try {
    return localStorage.getItem(KEY) === 'on';
  } catch {
    return false;
  }
}

export function setStoredCellScale(on: boolean): void {
  try {
    localStorage.setItem(KEY, on ? 'on' : 'off');
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // ignore (private mode, etc.)
  }
}

function subscribe(listener: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(EVENT, listener);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(EVENT, listener);
  };
}

const getSnapshot = (): boolean => getStoredCellScale();
/** SSR/prerender renders the default, or the markup mismatches on hydrate. */
const getServerSnapshot = (): boolean => false;

export function useMatrixCellScale(): { cellScale: boolean; setCellScale: (on: boolean) => void } {
  const cellScale = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const setCellScale = useCallback((on: boolean) => setStoredCellScale(on), []);
  return { cellScale, setCellScale };
}

/**
 * Circle diameter factor from a 0..1 strength, or null when strength is unknown.
 *
 * Floored at 0.18 rather than 0: a cell that EXISTS must stay visible, or the
 * view silently collapses "weak" into "missing" — the distinction it exists to
 * show. The floor is smaller than the rectangle version used because a circle at
 * 18% is still clearly a dot, whereas a rectangle at 18% read as a rendering
 * glitch. Square-rooted because AREA is what the eye reads as quantity: a linear
 * diameter makes a quarter-strength cell look like a sixteenth.
 */
export function scaleForStrength(strength: number | undefined): number | null {
  if (typeof strength !== 'number' || !Number.isFinite(strength)) return null;
  const clamped = Math.max(0, Math.min(1, strength));
  return 0.18 + 0.82 * Math.sqrt(clamped);
}
